import { spacing, tabletQuery } from '@globals/themes/themeConstants'
import { TenantMember } from '@kudos/http-client'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import UserAvatar from '@shared/avatar/UserAvatar'
import { getMentionPreviewRegionId } from '@shared/memberProfilePreview/memberProfilePreviewUtils'
import { getUserDisplayName } from '@shared/userDisplay/UserNameDisplay'
import { useT } from '@transifex/react'
import React from 'react'

type MentionsPopperProps = {
  tenantMemberId: string
  /**
   * Resolved by useHtmlContentWithMentions, which owns the single preview fetch and
   * all deleted-member handling. Null while the fetch is in flight or unresolved.
   */
  member: TenantMember | null
}

const MentionsPopper: React.FC<MentionsPopperProps> = ({ tenantMemberId, member }) => {
  const t = useT()
  const isTablet = useMediaQuery(tabletQuery)

  // Still loading, or the member could not be resolved (e.g. deleted). Render
  // nothing rather than an empty placeholder.
  if (!member) {
    return null
  }

  const { avatarUrls, firstName, lastName, publicStaffProfile } = member
  const fullName = getUserDisplayName(member, t)

  return (
    <Box
      component={Paper}
      id={getMentionPreviewRegionId(tenantMemberId)}
      role="region"
      aria-label={t('Profile preview')}
      sx={{ p: 2, borderRadius: spacing(2) }}>
      <Stack spacing={2}>
        <Stack useFlexGap spacing={2} direction="row" alignItems="center">
          <UserAvatar
            tenantMemberId={tenantMemberId}
            media={avatarUrls}
            firstName={firstName}
            lastName={lastName}
            size={36}
            enableProfilePreview={false}
          />
          <Typography variant="body2">{fullName}</Typography>
        </Stack>
        <Stack direction={isTablet ? 'column' : 'row'} useFlexGap spacing={1}>
          <Stack useFlexGap direction="row" spacing={1}>
            <WorkOutlineOutlinedIcon aria-hidden />
            <Typography>{publicStaffProfile?.jobTitle ?? '-'}</Typography>
          </Stack>
          <Stack useFlexGap direction="row" spacing={1}>
            <LocationOnOutlinedIcon aria-hidden />
            <Typography>{publicStaffProfile?.locations?.[0]?.name ?? '-'}</Typography>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  )
}

export default MentionsPopper
